import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { UserPlus, Loader2 } from "lucide-react";
import { useInviteGuestMutation, workspaceApi } from "../store/api/workspaces";
import { projectApi } from "../store/api/projects";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { handleApiError } from "../utils/apiErrors";

interface InviteGuestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  projectId: string;
  projectName: string;
}

export default function InviteGuestModal({
  open,
  onOpenChange,
  workspaceId,
  projectId,
  projectName,
}: InviteGuestModalProps) {
  const { t } = useLanguage();
  const dispatch = useDispatch();
  const [inviteGuest, { isLoading }] = useInviteGuestMutation();
  const dialogContentRef = useRef<HTMLDivElement>(null);
  
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const handleInvite = async () => {
    if (!email.trim() || !name.trim()) {
      toast.error(t('settings.inviteGuest.validation.required'));
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t('settings.inviteGuest.validation.invalidEmail'));
      return;
    }

    try {
      await inviteGuest({
        workspaceId,
        data: {
          email: email.trim(),
          name: name.trim(),
          projectId,
        }
      }).unwrap();
      
      // Invalidate caches to refresh member lists
      dispatch(projectApi.util.invalidateTags(['Project', 'ProjectMember']));
      dispatch(workspaceApi.util.invalidateTags(['WorkspaceMember']));
      
      toast.success(t('settings.inviteGuest.success').replace('{{name}}', name));
      setEmail("");
      setName("");
      onOpenChange(false);
    } catch (error: any) {
      handleApiError(error, t('settings.inviteGuest.error'));
    }
  };

  const handleClose = () => {
    setEmail("");
    setName("");
    onOpenChange(false);
  };

  // Scroll to top when modal opens
  useEffect(() => {
    if (open && dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={dialogContentRef} className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('settings.inviteGuest.title')}</DialogTitle>
          <DialogDescription>
            {t('settings.inviteGuest.description').replace('{{projectName}}', projectName)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="mb-2" htmlFor="guest-email">{t('settings.inviteGuest.email')} *</Label>
            <Input
              id="guest-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="guest@example.com"
              disabled={isLoading}
            />
          </div>

          <div>
            <Label className="mb-2" htmlFor="guest-name">{t('settings.inviteGuest.name')} *</Label>
            <Input
              id="guest-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('settings.inviteGuest.namePlaceholder')}
              disabled={isLoading}
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-900">
              <strong>{t('settings.inviteGuest.aboutTitle')}</strong>
              <br />
              • {t('settings.inviteGuest.about1')}
              <br />
              • {t('settings.inviteGuest.about2')}
              <br />
              • {t('settings.inviteGuest.about3')}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleInvite}
            disabled={isLoading || !email.trim() || !name.trim()}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <UserPlus className="w-4 h-4 mr-2" />
            )}
            {t('settings.inviteGuest.button')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
